/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import Fuse from 'fuse.js';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Groups3';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { CIColors } from 'shared';

import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { UserApiService } from '../../../../services/UserApiService';
import { useSelectEntityModalStyles } from '../../../../styles/selectEntityModal';
import { fetchSsoGroups } from '../../../../utils/queryFns';
import { useInviteMemberStyles } from '../InviteMember/InviteMember.style';

type Props = {
  organizationId: string;
  groupsThatAreAlreadyMembers: { groupName: string }[];
  open: boolean;
  onSuccess: (groupNames: string[]) => void;
  onCancel: () => void;
};

const fuseOptions = { shouldSort: true, ignoreLocation: true, threshold: 0 };

export const GroupPicker = memo<Props>(({ organizationId, groupsThatAreAlreadyMembers, open, onSuccess, onCancel }) => {
  const { t } = useTranslation();

  const classes = useInviteMemberStyles();
  const modalClasses = useSelectEntityModalStyles();

  const [customGroups, setCustomGroups] = useState<
    { groupName: string; users: { id: string; firstName: string; lastName: string; email: string }[] }[]
  >([]);
  const [customGroupError, setCustomGroupError] = useState<string | null>(null);

  const groupsQuery = useQuery([fetchSsoGroups.key, { organizationId }], fetchSsoGroups.fn);
  const groups = useMemo(
    () =>
      [...(groupsQuery.data?.payload.groups || []), ...customGroups]
        .filter(({ groupName }) => !groupsThatAreAlreadyMembers.find(g => g.groupName === groupName))
        .sort(),
    [groupsQuery.data?.payload.groups, customGroups, groupsThatAreAlreadyMembers],
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  useEffect(() => {
    setCustomGroupError(null);
  }, [searchQuery]);

  const visibleItems = useMemo(
    () =>
      searchQuery
        ? new Fuse(groups, { ...fuseOptions, keys: ['groupName'] }).search(searchQuery).map(r => r.item)
        : groups,
    [groups, searchQuery],
  );

  const handleToggle = useCallback(
    (item: (typeof visibleItems)[number]) => () =>
      setSelectedGroups(prevState => {
        const groups = prevState.filter(groupName => !(groupName === item.groupName));
        if (groups.length === prevState.length) {
          groups.push(item.groupName);
        }
        return groups;
      }),
    [setSelectedGroups],
  );

  const clear = useCallback(() => {
    setSearchQuery('');
    setSelectedGroups([]);
  }, [setSearchQuery, setSelectedGroups]);

  const handleSubmit = useCallback(() => {
    clear();
    onSuccess(selectedGroups);
  }, [clear, onSuccess, selectedGroups]);

  const handleClose = useCallback(() => {
    clear();
    onCancel();
  }, [clear, onCancel]);

  const addCustomGroup = useCallback(() => {
    if (groups.some(({ groupName }) => groupName === searchQuery)) {
      setCustomGroupError(t('organizationAdmin:teams.add_groups_modal.errors.already_exists'));
    } else if (groupsThatAreAlreadyMembers.some(({ groupName }) => groupName === searchQuery)) {
      setCustomGroupError(t('organizationAdmin:teams.add_groups_modal.errors.already_member'));
    } else {
      setCustomGroups(groups => [...groups, { groupName: searchQuery, users: [] }]);
      setSelectedGroups(groups => [...groups, searchQuery]);
      setCustomGroupError(null);
      setSearchQuery('');
    }
  }, [t, groups, groupsThatAreAlreadyMembers, searchQuery]);

  return (
    <HUModal2
      open={open}
      onClose={handleClose}
      size="medium"
      title={t('organizationAdmin:teams.add_groups')}
      paperClassName={modalClasses.modalPaper}
      footer={({ footerClass }) => (
        <div className={footerClass}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={selectedGroups.length === 0}
            data-cy="team-add-group-modal-submit"
          >
            {t('organizationAdmin:teams.add_groups_modal.submit', { count: selectedGroups.length })}
          </Button>

          <Button color="primary" variant="text" onClick={handleClose} data-cy="team-add-group-modal-cancel">
            {t(`common:modal.cancel`)}
          </Button>
        </div>
      )}
    >
      <div className={modalClasses.body}>
        <Stack direction="row" className={classes.textField}>
          <FormControl error={!!customGroupError} fullWidth>
            <OutlinedInput
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomGroup();
                }
              }}
              placeholder={t(`organizationAdmin:teams.add_groups_modal.search_or_add`)}
              data-cy="team-add-group-modal-search"
            />
            <FormHelperText>{customGroupError}</FormHelperText>
          </FormControl>

          <IconButton onClick={addCustomGroup} data-cy="add-custom-group">
            <AddIcon />
          </IconButton>
        </Stack>
        <List className={classes.list}>
          {!visibleItems.length ? (
            <ListItem key={searchQuery ? 'no results' : 'no groups'} dense>
              <ListItemText
                primary={
                  searchQuery
                    ? t('organizationAdmin:teams.add_groups_modal.no_search_results')
                    : t('organizationAdmin:teams.add_groups_modal.no_possible_groups')
                }
              />
            </ListItem>
          ) : (
            visibleItems.map(item => (
              <ListItem key={item.groupName} dense button onClick={handleToggle(item)}>
                <ListItemAvatar sx={{ display: 'flex', alignItems: 'center' }}>
                  <GroupIcon style={{ color: CIColors.Tertiary }} />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2">{item.groupName}</Typography>
                      <AvatarGroup max={5} sx={{ display: 'flex', justifyContent: 'left' }}>
                        {item.users.map(user => (
                          <Tooltip key={user.id} title={`${user.firstName} ${user.lastName} (${user.email})`}>
                            <Avatar
                              src={UserApiService.getUserByIdThumbnailUrl({ userId: user.id })}
                              alt={`${user.firstName} ${user.lastName}`}
                              data-cy={`teams-${item.groupName}-avatar-${user.firstName}-${user.lastName}`}
                              sx={{ width: 20, height: 20 }}
                            />
                          </Tooltip>
                        ))}
                      </AvatarGroup>
                    </Stack>
                  }
                />

                <ListItemIcon className={classes.listItemIcon}>
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={selectedGroups.some(groupName => groupName === item.groupName)}
                    tabIndex={-1}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    disableRipple
                    data-cy={`team-add-group-item-${item.groupName}`}
                  />
                </ListItemIcon>
              </ListItem>
            ))
          )}
        </List>
      </div>
    </HUModal2>
  );
});
